import {
  useCallback,
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  useState
} from 'react';
import './style.scss';

const SignatureCanvas = forwardRef(function SignatureCanvas(
  {
    className = '',
    height = 180,
    hint = 'Utilice su dedo o cursor sobre el recuadro para firmar',
    clearLabel = 'Limpiar Firma',
    onDrawChange
  },
  ref
) {
  const canvasRef = useRef(null);
  const isDrawingRef = useRef(false);
  const hasDrawnRef = useRef(false);
  const [hasDrawn, setHasDrawn] = useState(false);

  const updateHasDrawn = useCallback((value) => {
    hasDrawnRef.current = value;
    setHasDrawn(value);
    if (onDrawChange) {
      onDrawChange(value);
    }
  }, [onDrawChange]);

  const prepareContext = useCallback((canvas, preserveDataUrl = '') => {
    const rect = canvas.getBoundingClientRect();
    const ratio = window.devicePixelRatio || 1;
    const width = Math.max(1, Math.round(rect.width));
    const height = Math.max(1, Math.round(rect.height));

    canvas.width = Math.max(1, Math.round(width * ratio));
    canvas.height = Math.max(1, Math.round(height * ratio));

    const ctx = canvas.getContext('2d');
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.scale(ratio, ratio);
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, width, height);
    ctx.strokeStyle = '#2d3748';
    ctx.lineWidth = 2.5;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';

    if (preserveDataUrl) {
      const image = new Image();
      image.onload = () => {
        ctx.drawImage(image, 0, 0, width, height);
      };
      image.src = preserveDataUrl;
    }
  }, []);

  const clear = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    prepareContext(canvas);
    updateHasDrawn(false);
  }, [prepareContext, updateHasDrawn]);

  const getDataUrl = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return '';

    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = canvas.width;
    exportCanvas.height = canvas.height;

    const exportCtx = exportCanvas.getContext('2d');
    exportCtx.fillStyle = '#ffffff';
    exportCtx.fillRect(0, 0, exportCanvas.width, exportCanvas.height);
    exportCtx.drawImage(canvas, 0, 0);

    return exportCanvas.toDataURL('image/png');
  }, []);

  useImperativeHandle(
    ref,
    () => ({
      clear,
      hasDrawn: () => hasDrawn,
      toDataURL: getDataUrl
    }),
    [clear, getDataUrl, hasDrawn]
  );

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;

    const resizeCanvas = () => {
      const preserveDataUrl = hasDrawnRef.current ? getDataUrl() : '';
      prepareContext(canvas, preserveDataUrl);
    };

    resizeCanvas();
    const resizeObserver = typeof ResizeObserver !== 'undefined'
      ? new ResizeObserver(resizeCanvas)
      : null;

    if (resizeObserver && canvas.parentElement) {
      resizeObserver.observe(canvas.parentElement);
    }
    window.addEventListener('resize', resizeCanvas);

    return () => {
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
      window.removeEventListener('resize', resizeCanvas);
    };
  }, [getDataUrl, prepareContext]);

  function getPoint(event) {
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    return {
      x: event.clientX - rect.left,
      y: event.clientY - rect.top
    };
  }

  function startDrawing(event) {
    event.preventDefault();
    const canvas = canvasRef.current;
    if (!canvas) return;

    const point = getPoint(event);
    const ctx = canvas.getContext('2d');
    canvas.setPointerCapture?.(event.pointerId);
    ctx.beginPath();
    ctx.moveTo(point.x, point.y);
    isDrawingRef.current = true;
  }

  function draw(event) {
    if (!isDrawingRef.current) return;
    event.preventDefault();

    const canvas = canvasRef.current;
    if (!canvas) return;

    const point = getPoint(event);
    const ctx = canvas.getContext('2d');
    ctx.lineTo(point.x, point.y);
    ctx.stroke();

    if (!hasDrawn) {
      updateHasDrawn(true);
    }
  }

  function stopDrawing(event) {
    const canvas = canvasRef.current;
    if (canvas && event?.pointerId !== undefined && canvas.hasPointerCapture?.(event.pointerId)) {
      canvas.releasePointerCapture(event.pointerId);
    }
    isDrawingRef.current = false;
  }

  return (
    <div className={`signature-pad ${className}`.trim()}>
      <div className='signature-pad__box' style={{ height }}>
        <canvas
          ref={canvasRef}
          className='signature-pad__canvas'
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerCancel={stopDrawing}
          onPointerLeave={stopDrawing}
        />
      </div>
      <div className='signature-pad__controls'>
        <span className='signature-pad__hint'>{hint}</span>
        <button type='button' onClick={clear} className='signature-pad__clear'>
          <span className='material-symbols-sharp'>delete_sweep</span>
          <span>{clearLabel}</span>
        </button>
      </div>
    </div>
  );
});

export default SignatureCanvas;
